import { useEffect, useRef, useState } from "react";

const PREFIX = "adam.resendCooldownUntil.";

function readRemaining(key: string): number {
  try {
    const until = Number(sessionStorage.getItem(key) ?? 0);
    return Math.max(0, Math.ceil((until - Date.now()) / 1000));
  } catch {
    return 0;
  }
}

/**
 * Countdown for "resend email" buttons. The expiry instant is persisted in
 * sessionStorage (keyed by `scope`), so a page reload — or the same surface
 * open in another tab — doesn't reset the timer and tempt a double send.
 * The backend throttle stays the real guard; this is purely UX.
 */
export function useResendCooldown(scope: string, seconds = 30) {
  const key = `${PREFIX}${scope}`;
  const [cooldown, setCooldown] = useState(() => readRemaining(key));
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (cooldown <= 0) {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
      return;
    }
    timer.current = window.setTimeout(() => setCooldown(readRemaining(key)), 1000);
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [cooldown, key]);

  const start = () => {
    try {
      sessionStorage.setItem(key, String(Date.now() + seconds * 1000));
    } catch {
      // Storage unavailable — fall back to in-memory countdown only.
    }
    setCooldown(seconds);
  };

  return { cooldown, start };
}
